// SPDX-License-Identifier: AGPL-3.0-only

import { requestJson } from "./http.js";

export interface ProbeEnrollmentTicket {
  readonly id: string;
  readonly deploymentCode: string;
  readonly displayCode: string;
  readonly expiresAt: string;
}

export type ProbeEnrollmentStatus = "created" | "bound" | "activated" | "expired" | "revoked";

export interface ProbeEnrollmentState {
  readonly id: string;
  readonly tenantId: string;
  readonly status: ProbeEnrollmentStatus;
  readonly expiresAt: string;
  readonly probeId: string | null;
}

export interface CreateProbeEnrollmentInput {
  readonly requestedDisplayName?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readString(value: unknown, fieldName: string): string {
  if (typeof value !== "string") {
    throw new Error(`Probe enrollment response field ${fieldName} is invalid`);
  }
  return value;
}

function readProbeEnrollmentTicket(payload: unknown): ProbeEnrollmentTicket {
  if (!isRecord(payload)) {
    throw new Error("Probe enrollment response is invalid");
  }

  return {
    id: readString(payload.id, "id"),
    deploymentCode: readString(payload.deploymentCode, "deploymentCode"),
    displayCode: readString(payload.displayCode, "displayCode"),
    expiresAt: readString(payload.expiresAt, "expiresAt"),
  };
}

function readProbeEnrollmentState(payload: unknown): ProbeEnrollmentState {
  if (!isRecord(payload)) {
    throw new Error("Probe enrollment response is invalid");
  }
  const status: string = readString(payload.status, "status");
  if (
    status !== "created" &&
    status !== "bound" &&
    status !== "activated" &&
    status !== "expired" &&
    status !== "revoked"
  ) {
    throw new Error("Probe enrollment response field status is invalid");
  }
  if (payload.probeId !== null && typeof payload.probeId !== "string") {
    throw new Error("Probe enrollment response field probeId is invalid");
  }

  return {
    id: readString(payload.id, "id"),
    tenantId: readString(payload.tenantId, "tenantId"),
    status,
    expiresAt: readString(payload.expiresAt, "expiresAt"),
    probeId: payload.probeId,
  };
}

export async function createProbeEnrollment(
  tenantId: string,
  input: CreateProbeEnrollmentInput,
): Promise<ProbeEnrollmentTicket> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-enrollments`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );

  return readProbeEnrollmentTicket(payload);
}

export async function readProbeEnrollment(
  tenantId: string,
  enrollmentId: string,
): Promise<ProbeEnrollmentState> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-enrollments/${encodeURIComponent(enrollmentId)}`,
  );

  return readProbeEnrollmentState(payload);
}

export async function commitProbeEnrollmentDisplayName(
  tenantId: string,
  enrollmentId: string,
  displayName: string,
): Promise<void> {
  await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-enrollments/${encodeURIComponent(enrollmentId)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ displayName }),
    },
  );
}

export async function revokeProbeEnrollment(
  tenantId: string,
  enrollmentId: string,
): Promise<ProbeEnrollmentState> {
  const payload: unknown = await requestJson(
    `/api/v1/tenants/${encodeURIComponent(tenantId)}/probe-enrollments/${encodeURIComponent(enrollmentId)}`,
    { method: "DELETE" },
  );

  return readProbeEnrollmentState(payload);
}
